import * as React from 'react'
import { useState, useEffect, MutableRefObject, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import SelectField from '../form/SelectField'
import { Field, FieldProps, FormikHelpers, FormikProps, FormikValues } from 'formik'
import { TValues } from '../profile/profileEdit'
import { getCountries, getRegions } from '../../api/location'

interface Props {
    initialCountry: string
    initialRegion: string
    currentCountry?: React.MutableRefObject<string | null>
    currentRegion?: React.MutableRefObject<string | null>
    formikRef?: MutableRefObject<FormikProps<TValues>>
}

type Location = {
    name: string
    fr: string
    code: string
    type?: string
    topLine?: boolean
}

const LocationSelect: React.FunctionComponent<Props> = (props: Props) => {
    const { initialCountry, currentCountry, initialRegion, currentRegion, formikRef } = props
    const [t] = useTranslation()

    const [countries, setCountries] = useState<FormikValues[]>([])
    const [regions, setRegions] = useState<FormikValues[]>([])
    const isCountryChanged = useRef<boolean>(false)

    function rearrangeObjects(array: { value: string; label: string; type?: string }[]) {
        const metropolitan = array.filter((obj) => obj.type === 'metropolitan')

        const overseas = array.filter((obj) => obj.type === 'overseas')

        if (!metropolitan.length || !overseas.length) return array

        if (overseas.length) {
            // @ts-ignore
            overseas[0].topLine = true
        }
        return metropolitan.concat(overseas)
    }

    const fetchRegions = (country: string) => {
        getRegions({ country }).then((response) => {
            try {
                setRegions([
                    ...response.map((region: Location) => ({
                        value: region.code,
                        label: region.name,
                        type: region.type || null
                    }))
                ])
                // @ts-ignore
                setRegions((regions) => rearrangeObjects(regions))

                const getCurrentRegionValue = () => {
                    const currentR = response.find((elem) => elem.name === initialRegion)
                    if (currentR) return currentR.code
                    return null
                }
                if (formikRef && !isCountryChanged.current)
                    formikRef.current.setFieldValue('region', getCurrentRegionValue())
            } catch (e) {
                console.log(e)
            }
        })
    }

    const onCountrySelect = (country: string, form: FormikHelpers<FormikValues>): void => {
        fetchRegions(country)
        isCountryChanged.current = true
        form.setFieldValue('region', null)
    }

    useEffect(() => {
        getCountries().then((response) => {
            try {
                setCountries([
                    ...response.map((country: Location) => ({
                        value: country.code,
                        label: country.name
                    }))
                ])
            } catch (e) {
                console.log(e)
            }
        })
    }, [])

    useEffect(() => {
        if (countries.length) {
            // @ts-ignore
            const countryCode = countries.find((elem) => elem.label === initialCountry)
            if (countryCode) {
                fetchRegions(countryCode.value)
                if (formikRef && !isCountryChanged.current)
                    formikRef.current.setFieldValue('country', countryCode.value)
            }
        }
        // eslint-disable-next-line
    }, [countries])

    return (
        <>
            <Field name="country">
                {({ field, form }: FieldProps) => (
                    <SelectField
                        form={form}
                        field={field}
                        options={countries}
                        label={t('Country')}
                        onChangeCallback={onCountrySelect}
                        chosenLabel={currentCountry}
                    />
                )}
            </Field>
            <Field name="region">
                {({ field, form }: FieldProps) => (
                    <SelectField
                        form={form}
                        field={field}
                        label={t('Region')}
                        options={regions}
                        chosenLabel={currentRegion}
                    />
                )}
            </Field>
        </>
    )
}

export default LocationSelect
